import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLanguage } from "../i18n";
import { Reveal } from "./Reveal";

export const MenuItem = ({ item }) => (
  <div className="group py-4 border-b border-line" data-testid={`menu-item-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
    <div className="flex items-baseline justify-between gap-4">
      <h4 className="font-serif text-lg md:text-xl text-ink group-hover:text-terra transition-colors duration-300">{item.name}</h4>
      <span className="shrink-0 font-accent text-sm tracking-wider text-gold">€ {item.price}</span>
    </div>
    {item.desc && <p className="mt-1 text-sm text-muted2 font-light leading-relaxed">{item.desc}</p>}
  </div>
);

export const MenuBlock = ({ id, kicker, title, note, categories, twoCols = true }) => {
  const { lang, t } = useLanguage();
  const [active, setActive] = useState(categories[0].id);
  const cat = categories.find((c) => c.id === active);

  return (
    <section id={id} data-testid={`${id}-section`} className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32 scroll-mt-20">
      <Reveal>
        {kicker && <p className="text-xs tracking-[0.35em] uppercase text-terra mb-4">{kicker}</p>}
        <h2 className="font-serif text-4xl md:text-6xl text-ink" data-testid={`${id}-title`}>{title}</h2>
        {note && <p className="mt-4 max-w-2xl text-sm text-muted2 font-light italic">{note}</p>}
      </Reveal>

      <Reveal delay={0.1} className="mt-12">
        <div className="flex flex-wrap gap-2" data-testid={`${id}-category-tabs`}>
          {categories.map((c) => (
            <button
              key={c.id}
              onClick={() => setActive(c.id)}
              data-testid={`${id}-tab-${c.id}`}
              className={`rounded-full px-5 py-2.5 text-xs tracking-[0.16em] uppercase transition-colors duration-300 border ${
                active === c.id
                  ? "bg-terra text-cream border-terra font-semibold"
                  : "border-line bg-surface text-ink/60 hover:border-terra/60 hover:text-terra"
              }`}
            >
              {lang === "it" ? c.it : c.en}
              <span className="ml-2 opacity-60">{c.items.length}</span>
            </button>
          ))}
        </div>
      </Reveal>

      <AnimatePresence mode="wait">
        <motion.div
          key={active}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className={`mt-10 ${twoCols ? "md:columns-2 md:gap-14" : "max-w-3xl"}`}
          data-testid={`${id}-items-${active}`}
        >
          {cat.items.map((item) => (
            <div key={item.name} className="break-inside-avoid">
              <MenuItem item={item} />
            </div>
          ))}
        </motion.div>
      </AnimatePresence>
      {note && <p className="sr-only">{t("menu_note")}</p>}
    </section>
  );
};
